import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Stack, Paper, Grid, Button, Chip, Stepper, Step, StepLabel,
  Card, CardContent, CardActionArea, Checkbox, FormControlLabel, FormGroup,
  TextField, Radio, RadioGroup, FormControl, FormLabel, LinearProgress,
  Alert, Snackbar, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, InputLabel, Divider, Tooltip, CircularProgress, Autocomplete,
  InputAdornment, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PaletteIcon from '@mui/icons-material/Palette';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import FaceIcon from '@mui/icons-material/Face';
import MailIcon from '@mui/icons-material/Mail';
import HotelIcon from '@mui/icons-material/Hotel';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import FlightIcon from '@mui/icons-material/Flight';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PeopleIcon from '@mui/icons-material/People';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import BadgeIcon from '@mui/icons-material/Badge';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import StarIcon from '@mui/icons-material/Star';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PersonIcon from '@mui/icons-material/Person';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import {
  SERVICE_CATEGORY_TILES_QUERY,
  QUESTIONNAIRE_QUESTIONS_QUERY,
  SAVE_QUESTIONNAIRE_MUTATION,
  COMPLETE_QUESTIONNAIRE_MUTATION,
  CREATE_SERVICE_WISHLIST_MUTATION,
  MY_SERVICE_WISHLISTS_QUERY,
  CONFIRM_SERVICE_WISHLIST_MUTATION,
  SUBMIT_WISHLIST_FOR_APPROVAL_MUTATION,
  CONVERT_WISHLIST_TO_CONTRACT_MUTATION,
  DUPLICATE_SERVICE_WISHLIST_MUTATION,
  USERS_QUERY,
  WISH_LIST_TEMPLATES_QUERY,
  FAMILY_BUDGET_PLAN_QUERY,
  SERVICE_ESTIMATES_QUERY,
} from '../graphql/queries';
import ContractPrintView from '../components/ContractPrintView';
import ServiceShowcase, { AutoVendorPicker } from '../components/ServiceShowcase';
import { EventCityField, DEFAULT_RADIUS } from '../components/LocationFilter';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const ICON_MAP = {
  LocationOn: LocationOnIcon,
  Restaurant: RestaurantIcon,
  Palette: PaletteIcon,
  CameraAlt: CameraAltIcon,
  MusicNote: MusicNoteIcon,
  DirectionsCar: DirectionsCarIcon,
  TempleHindu: TempleHinduIcon,
  Face: FaceIcon,
  Mail: MailIcon,
  Hotel: HotelIcon,
  CardGiftcard: CardGiftcardIcon,
  Flight: FlightIcon,
  AutoFixHigh: AutoFixHighIcon,
  EventNote: EventNoteIcon,
  People: PeopleIcon,
  EventSeat: EventSeatIcon,
  Badge: BadgeIcon,
  MoreHoriz: MoreHorizIcon,
  LocalParking: LocalParkingIcon,
  Star: StarIcon,
};

// The budget is asked before any service is chosen; bride/groom families start at BUDGET
// and split across the chosen services before their questions
const STEPS = ['Select For Whom', 'Wedding Budget', 'Choose Services', 'Split Budget', 'Answer Questions', 'Review & Confirm'];
const STEP = { WHO: 0, BUDGET: 1, SERVICES: 2, SPLIT: 3, QUESTIONS: 4, REVIEW: 5 };

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const ACTIVE_WISHLIST_STATUSES = ['draft', 'pending_confirmation', 'pending_approval', 'approved', 'contracted'];

// "Priya Sundaram" (or the email when the family has no profile yet)
const personName = (u) => (u?.profile ? `${u.profile.firstName} ${u.profile.lastName}`.trim() : u?.email || '');

export default function WishlistWizardPage() {
  const { user } = useAuth();
  const roleName = user?.role?.name;
  const isContractor = roleName === 'vendor' || roleName === 'matchmaker' || roleName === 'system_admin';
  const isBride = roleName === 'bride_family';
  const isGroom = roleName === 'groom_family';

  // Bride/groom auto-set forWhom and skip step 0
  const autoForWhom = isBride ? 'bride' : isGroom ? 'groom' : '';
  const initialStep = (isBride || isGroom) ? STEP.BUDGET : STEP.WHO;

  const [activeStep, setActiveStep] = useState(initialStep);
  const [forWhom, setForWhom] = useState(autoForWhom);
  const [selectedClient, setSelectedClient] = useState(null);
  // Only used when forWhom === 'both': selectedClient holds the bride, this holds the groom.
  const [selectedGroomClient, setSelectedGroomClient] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentCategoryIdx, setCurrentCategoryIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // { categoryName: { questionId: answer } }
  const [serviceSelections, setServiceSelections] = useState({}); // { categoryName: [serviceIds] }
  const [eventDetails, setEventDetails] = useState({ eventDate: '', eventScale: '', guestCount: '' });
  // Overall wedding budget and each selected service's budget ({ categoryName: amount }),
  // saved to the family's Budget page with the wishlist
  const [familyBudget, setFamilyBudget] = useState('');
  const [serviceBudgets, setServiceBudgets] = useState({});
  // Where the function takes place: vendors are shown by distance from it
  const [eventCity, setEventCity] = useState('');
  // Keywords for picking vendors ("banana leaf", "jasmine", "candid"...), matched with the answers
  const [keywords, setKeywords] = useState([]);
  // Preferred vendor per service ({ categoryName: { offeringId, packageId, title, packageName, ..., auto } }):
  // picked automatically until the family chooses one themselves
  const [preferredVendors, setPreferredVendors] = useState({});
  // Split Budget step: which services show what they include ({ categoryName: true })
  const [showcaseOpen, setShowcaseOpen] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [showWishlists, setShowWishlists] = useState(false);
  const [confirmNewDialog, setConfirmNewDialog] = useState(false);
  // Optional "start from a template" — matchmaker/admin only. templateItems is an
  // editable copy of the chosen template's checklist, merged into the submission
  // as an extra category alongside whatever categories/services are picked below.
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateItems, setTemplateItems] = useState([]); // [{ task, selected }]
  const [contractView, setContractView] = useState({ open: false, contract: null });
  // Matchmaker/admin only: lets them redirect a duplicated wishlist to a
  // different bride/groom/both instead of always re-copying the same client.
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, wishlist: null, forWhom: '', client: null, groomClient: null });

  const { data: tilesData, loading: tilesLoading } = useQuery(SERVICE_CATEGORY_TILES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: wishlistsData, loading: wishlistsLoading, refetch: refetchWishlists } = useQuery(MY_SERVICE_WISHLISTS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: usersData } = useQuery(USERS_QUERY, { skip: !isContractor });
  const clientUsers = (usersData?.users || []).filter(u =>
    ['bride_family', 'groom_family'].includes(u.role?.name)
  );
  const { data: templatesData } = useQuery(WISH_LIST_TEMPLATES_QUERY, { skip: !isContractor });
  const wishlistTemplates = templatesData?.wishlistTemplates || [];

  // Start the budget questions from what the family already set (the bride's for a joint wishlist)
  const budgetFamilyId = isContractor ? selectedClient?.id : user?.id;
  const { data: planData, refetch: refetchPlan } = useQuery(FAMILY_BUDGET_PLAN_QUERY, {
    variables: { familyUserId: budgetFamilyId },
    skip: !budgetFamilyId,
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    const plan = planData?.familyBudgetPlan;
    if (!plan) return;
    setFamilyBudget(plan.totalBudget > 0 ? String(plan.totalBudget) : '');
    setEventCity(plan.eventCity || '');
    setServiceBudgets(Object.fromEntries(Object.entries(plan.serviceBudgets || {}).map(([cat, amount]) => [cat, String(amount)])));
  }, [planData]);
  const totalBudgetNum = Number(familyBudget) || 0;
  const allocatedBudget = selectedCategories.reduce((s, cat) => s + (Number(serviceBudgets[cat]) || 0), 0);
  const guestCountNum = parseInt(eventDetails.guestCount, 10) || null;

  // Typical cost of each service for this function (per-plate prices × guests), essentials first
  const { data: estimatesData } = useQuery(SERVICE_ESTIMATES_QUERY, {
    variables: { guestCount: guestCountNum, eventCity: eventCity.trim() || null },
    skip: activeStep < STEP.SERVICES,
    fetchPolicy: 'cache-and-network',
  });
  const estimates = estimatesData?.serviceEstimates || [];
  const estimateOf = (cat) => estimates.find(e => e.category === cat);
  const estimatedSelected = selectedCategories.reduce((s, cat) => s + (estimateOf(cat)?.typicalCost || 0), 0);

  // Ticks the most essential services whose typical cost still fits the budget, on top of any
  // already selected
  const suggestServices = () => {
    let left = totalBudgetNum - estimatedSelected;
    const added = [];
    for (const e of estimates) {
      if (selectedCategories.includes(e.category) || !tiles.some(t => t.category === e.category)) continue;
      if (!(e.typicalCost > 0) || e.typicalCost > left) continue;
      added.push(e.category);
      left -= e.typicalCost;
    }
    if (!added.length) {
      showSnack('No more services fit the remaining budget at their typical cost', 'info');
      return;
    }
    setSelectedCategories(prev => [...prev, ...added]);
    showSnack(`Suggested ${added.length} service${added.length === 1 ? '' : 's'} — ${inr(totalBudgetNum - left)} estimated of your ${inr(totalBudgetNum)} budget`);
  };

  // The automatic pick replaces only an automatic one, never a vendor the family chose
  const handleAutoPick = (cat, pick) => setPreferredVendors(prev => {
    if (prev[cat] && !prev[cat].auto) return prev;
    const next = { ...prev };
    if (pick) next[cat] = { ...pick, auto: true }; else delete next[cat];
    return next;
  });
  const chooseVendor = (cat, choice) => setPreferredVendors(prev => ({ ...prev, [cat]: { ...choice, auto: false } }));
  const resetVendorPick = (cat) => setPreferredVendors(prev => { const next = { ...prev }; delete next[cat]; return next; });

  const [saveQuestionnaire] = useMutation(SAVE_QUESTIONNAIRE_MUTATION);
  const [completeQuestionnaire] = useMutation(COMPLETE_QUESTIONNAIRE_MUTATION);
  const [createWishlist] = useMutation(CREATE_SERVICE_WISHLIST_MUTATION);
  const [confirmWishlist] = useMutation(CONFIRM_SERVICE_WISHLIST_MUTATION);
  const [submitForApproval] = useMutation(SUBMIT_WISHLIST_FOR_APPROVAL_MUTATION);
  const [convertToContract] = useMutation(CONVERT_WISHLIST_TO_CONTRACT_MUTATION);
  const [duplicateWishlist] = useMutation(DUPLICATE_SERVICE_WISHLIST_MUTATION);

  const tiles = tilesData?.serviceCategoryTiles || [];
  const wishlists = wishlistsData?.myServiceWishlists || [];

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // Whether the given user (self or a selected client) already has an active
  // wishlist/contract in flight, based on wishlists this matchmaker can see.
  // Checks both sides since a "both families" wishlist stores the groom
  // separately from the bride (userId vs groomUserId).
  const hasActiveWishlistFor = (targetUserId) => wishlists.some(wl =>
    (wl.userId === targetUserId || wl.groomUserId === targetUserId) && ACTIVE_WISHLIST_STATUSES.includes(wl.status)
  );

  const clientLabel = (client) => client?.profile?.firstName
    ? `${client.profile.firstName} ${client.profile.lastName || ''}`.trim()
    : client?.email;

  // Step 1: For Whom selection
  const handleForWhomSelect = (value) => {
    setForWhom(value);
    setActiveStep(STEP.BUDGET);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    if (client) {
      // Auto-set forWhom based on client role
      const autoFor = client.role?.name === 'bride_family' ? 'bride' : 'groom';
      setForWhom(autoFor);
      // Only warn about replacing a wishlist if THIS client already has one —
      // not based on any of the matchmaker's other clients.
      if (hasActiveWishlistFor(client.id)) {
        setConfirmNewDialog(true);
      } else {
        setActiveStep(STEP.BUDGET);
      }
    }
  };

  // "Both Families" needs a distinct bride AND groom account selected — this
  // just switches step 0 into that dual-picker mode; handleBothFamiliesContinue
  // advances once both are chosen.
  const handleBothFamiliesClick = () => {
    setForWhom('both');
    setSelectedClient(null);
    setSelectedGroomClient(null);
  };

  const handleCancelBothFamilies = () => {
    setForWhom('');
    setSelectedClient(null);
    setSelectedGroomClient(null);
  };

  const handleBothFamiliesContinue = () => {
    if (!selectedClient || !selectedGroomClient) {
      showSnack('Select both a bride and a groom to continue', 'warning');
      return;
    }
    if (selectedClient.id === selectedGroomClient.id) {
      showSnack('Bride and groom must be different clients', 'warning');
      return;
    }
    if (hasActiveWishlistFor(selectedClient.id) || hasActiveWishlistFor(selectedGroomClient.id)) {
      setConfirmNewDialog(true);
    } else {
      setActiveStep(STEP.BUDGET);
    }
  };

  // Step 2: the total wedding budget (and, for matchmakers, the optional checklist template)
  const proceedToServices = () => {
    if (!(totalBudgetNum > 0)) {
      showSnack('Enter the total wedding budget to continue', 'warning');
      return;
    }
    if (!eventCity.trim()) {
      showSnack('Enter where the function will take place to continue', 'warning');
      return;
    }
    setActiveStep(STEP.SERVICES);
  };

  // Optional "start from a template" (matchmaker/admin only). Loads the
  // template's checklist as pre-checked items the matchmaker can prune before
  // it gets merged into the submission alongside any manually-picked services.
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const tmpl = wishlistTemplates.find(t => t.id === templateId);
    setTemplateItems(tmpl ? (tmpl.items || []).map(i => ({ task: i.task, selected: true, cost: Number(i.cost) || 0 })) : []);
  };

  const toggleTemplateItem = (idx) => {
    setTemplateItems(prev => prev.map((item, i) => i === idx ? { ...item, selected: !item.selected } : item));
  };

  // Step 2: Category selection (checkboxes as tiles)
  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const proceedToSplit = () => {
    if (selectedCategories.length === 0) {
      if (templateItems.some(i => i.selected)) {
        // Nothing manually picked, but the template checklist alone is enough
        // to submit — skip straight to review instead of forcing a category pick.
        setActiveStep(STEP.REVIEW);
        return;
      }
      showSnack('Please select at least one service category or a template', 'warning');
      return;
    }
    setShowcaseOpen({ [selectedCategories[0]]: true }); // open the first so it's discoverable
    // Start each service without a budget from its typical cost
    setServiceBudgets(prev => {
      const next = { ...prev };
      selectedCategories.forEach(cat => {
        const typical = estimateOf(cat)?.typicalCost;
        if (!(Number(next[cat]) > 0) && typical > 0) next[cat] = String(Math.round(typical));
      });
      return next;
    });
    setActiveStep(STEP.SPLIT);
  };

  // Step 4: split the total budget across the selected services
  const displayNameOf = (cat) => tiles.find(t => t.category === cat)?.displayName || cat;

  const splitEqually = () => {
    const share = Math.floor(totalBudgetNum / selectedCategories.length);
    const remainder = totalBudgetNum - share * selectedCategories.length;
    setServiceBudgets(prev => ({
      ...prev,
      ...Object.fromEntries(selectedCategories.map((cat, i) => [cat, String(i === 0 ? share + remainder : share)])),
    }));
  };

  const proceedToQuestions = () => {
    if (!(totalBudgetNum > 0)) {
      showSnack('Enter the total wedding budget to continue', 'warning');
      return;
    }
    const missing = selectedCategories.filter(cat => !(Number(serviceBudgets[cat]) > 0));
    if (missing.length) {
      showSnack(`Enter a budget for ${missing.map(displayNameOf).join(', ')}`, 'warning');
      return;
    }
    if (allocatedBudget > totalBudgetNum) {
      showSnack(`The services are ${inr(allocatedBudget - totalBudgetNum)} over the total budget — reduce them or raise the total`, 'warning');
      return;
    }
    setCurrentCategoryIdx(0);
    setActiveStep(STEP.QUESTIONS);
  };

  // Step 5: Q&A per category (tile-by-tile)
  const currentCategory = selectedCategories[currentCategoryIdx];
  const currentTile = tiles.find(t => t.category === currentCategory);

  const handleAnswer = (categoryName, questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [categoryName]: { ...(prev[categoryName] || {}), [questionId]: value },
    }));
  };

  const setCategoryAnswers = (categoryName, next) => {
    setAnswers(prev => ({ ...prev, [categoryName]: next }));
  };

  const handleMultiAnswer = (categoryName, questionId, option) => {
    setAnswers(prev => {
      const existing = prev[categoryName]?.[questionId] || [];
      const updated = existing.includes(option)
        ? existing.filter(o => o !== option)
        : [...existing, option];
      return { ...prev, [categoryName]: { ...(prev[categoryName] || {}), [questionId]: updated } };
    });
  };

  const toggleServiceSelection = (categoryName, serviceId) => {
    setServiceSelections(prev => {
      const existing = prev[categoryName] || [];
      const updated = existing.includes(serviceId)
        ? existing.filter(id => id !== serviceId)
        : [...existing, serviceId];
      return { ...prev, [categoryName]: updated };
    });
  };

  const nextCategory = () => {
    // Each category needs at least one service picked, or it would reach the contract empty
    if ((currentTile?.services || []).length && !(serviceSelections[currentCategory] || []).length) {
      showSnack(`Pick at least one ${currentTile.displayName} service to continue`, 'warning');
      return;
    }
    if (currentCategoryIdx < selectedCategories.length - 1) {
      setCurrentCategoryIdx(prev => prev + 1);
    } else {
      setActiveStep(STEP.REVIEW);
    }
  };

  const prevCategory = () => {
    if (currentCategoryIdx > 0) {
      setCurrentCategoryIdx(prev => prev - 1);
    } else {
      setActiveStep(STEP.SPLIT);
    }
  };

  // Step 4: Review & Submit
  const handleSubmitWishlist = async () => {
    try {
      const selections = selectedCategories.map(cat => {
        const tile = tiles.find(t => t.category === cat);
        const selectedSvcs = serviceSelections[cat] || [];
        return {
          category: cat,
          categoryName: tile?.displayName || cat,
          services: (tile?.services || [])
            .filter(s => selectedSvcs.includes(s.id))
            .map(s => ({ id: s.id, name: s.name, selected: true, budget: Number(s.basePrice) })),
          answers: answers[cat] || {},
          serviceBudget: Number(serviceBudgets[cat]) || 0,
          // Only the ids are kept by the server; it fills in the rest from the live offering
          ...(preferredVendors[cat] ? { preferredVendor: { offeringId: preferredVendors[cat].offeringId, packageId: preferredVendors[cat].packageId, auto: preferredVendors[cat].auto } } : {}),
        };
      });

      // Merge in the template checklist (if one was picked) as its own category.
      // Items aren't tied to a WeddingService, so their price comes from the
      // template's own per-item cost (set by admin in Wishlist Templates).
      const chosenTemplate = wishlistTemplates.find(t => t.id === selectedTemplateId);
      const selectedTemplateItems = templateItems.filter(i => i.selected);
      if (chosenTemplate && selectedTemplateItems.length > 0) {
        selections.push({
          // `category` doubles as the ContractServiceItem's serviceCategory once
          // this reaches the contract — keep it human-readable, not a slug.
          category: chosenTemplate.name,
          categoryName: `${chosenTemplate.name} (Template)`,
          services: selectedTemplateItems.map(i => ({ id: null, name: i.task, selected: true, budget: Number(i.cost) || 0 })),
          answers: {},
        });
      }

      const totalBudget = selections.reduce((sum, cat) =>
        sum + cat.services.reduce((s, svc) => s + (svc.budget || 0), 0), 0
      );

      await createWishlist({
        variables: {
          input: {
            forWhom,
            clientId: selectedClient?.id || null,
            groomClientId: forWhom === 'both' ? (selectedGroomClient?.id || null) : null,
            serviceSelections: selections,
            totalBudget: totalBudget || null,
            familyBudget: totalBudgetNum,
            eventCity: eventCity.trim() || null,
            eventDate: eventDetails.eventDate || null,
            eventScale: eventDetails.eventScale || null,
            guestCount: eventDetails.guestCount ? parseInt(eventDetails.guestCount) : null,
          }
        }
      });

      showSnack('Service wishlist created successfully! It will be sent for admin approval.');
      refetchWishlists();
      setShowWishlists(true);
      // Reset wizard
      setActiveStep(initialStep);
      setForWhom(autoForWhom);
      setSelectedClient(null);
      setSelectedGroomClient(null);
      setSelectedCategories([]);
      setAnswers({});
      setServiceSelections({});
      setSelectedTemplateId('');
      setTemplateItems([]);
      setFamilyBudget('');
      setServiceBudgets({});
      setEventCity('');
      setKeywords([]);
      setPreferredVendors({});
      if (!isContractor) refetchPlan(); // the next wishlist starts from the budgets just saved
    } catch (err) {
      showSnack(err.message, 'error');
    }
  };

  const handleConfirm = async (wishlistId) => {
    try {
      const role = isBride ? 'bride' : isGroom ? 'groom' : 'contractor';
      await confirmWishlist({ variables: { id: wishlistId, role } });
      showSnack('Wishlist confirmed and submitted for admin approval!');
      refetchWishlists();
    } catch (err) {
      showSnack(err.message, 'error');
    }
  };

  const handleSubmitForApproval = async (wishlistId) => {
    try {
      await submitForApproval({ variables: { id: wishlistId } });
      showSnack('Wishlist submitted for admin approval!');
      refetchWishlists();
    } catch (err) {
      showSnack(err.message, 'error');
    }
  };

  const handleConvertToContract = async (wishlistId) => {
    try {
      await convertToContract({ variables: { wishlistId } });
      showSnack('Contract created from wishlist!');
      refetchWishlists();
    } catch (err) {
      showSnack(err.message, 'error');
    }
  };

  // Matchmakers/admins get asked who the duplicate is for (defaulting to the
  // original's bride/groom/both); everyone else just duplicates for themselves.
  const handleDuplicateWishlist = async (wl) => {
    if (!isContractor) {
      try {
        await duplicateWishlist({ variables: { id: wl.id } });
        showSnack('Wishlist duplicated as draft!');
        refetchWishlists();
      } catch (err) {
        showSnack(err.message, 'error');
      }
      return;
    }
    setDuplicateDialog({
      open: true,
      wishlist: wl,
      forWhom: wl.forWhom,
      client: clientUsers.find(u => u.id === wl.userId) || null,
      groomClient: clientUsers.find(u => u.id === wl.groomUserId) || null,
    });
  };

  const handleConfirmDuplicate = async () => {
    const { wishlist, forWhom, client, groomClient } = duplicateDialog;
    if (!client) {
      showSnack(forWhom === 'both' ? 'Select the bride' : 'Select a client', 'warning');
      return;
    }
    if (forWhom === 'both' && (!groomClient || groomClient.id === client.id)) {
      showSnack('Select a distinct bride and groom', 'warning');
      return;
    }
    try {
      await duplicateWishlist({
        variables: {
          id: wishlist.id,
          forWhom,
          clientId: client.id,
          groomClientId: forWhom === 'both' ? groomClient.id : null,
        },
      });
      showSnack('Wishlist duplicated as draft!');
      refetchWishlists();
      setDuplicateDialog({ open: false, wishlist: null, forWhom: '', client: null, groomClient: null });
    } catch (err) {
      showSnack(err.message, 'error');
    }
  };

  // Check if the current user themselves has an active wishlist/contract.
  // Matchmakers act on behalf of many clients, so this only applies to the
  // bride/groom self-service flow — a matchmaker's "replace" check is scoped
  // to whichever client they pick, not their whole client portfolio.
  const hasActiveWishlist = !isContractor && hasActiveWishlistFor(user.id);

  const handleNewWishlistClick = () => {
    if (isContractor) {
      // Jump straight into the wizard to pick who this wishlist is for.
      // Whether a "replace existing" warning is needed is decided per client
      // once one is selected (see handleClientSelect).
      setSelectedClient(null);
      setSelectedGroomClient(null);
      setForWhom('');
      setSelectedTemplateId('');
      setTemplateItems([]);
      setFamilyBudget('');
      setServiceBudgets({});
      setEventCity('');
      setKeywords([]);
      setPreferredVendors({});
      setActiveStep(STEP.WHO);
      setShowWishlists(false);
      return;
    }
    if (hasActiveWishlist) {
      setConfirmNewDialog(true);
    } else {
      setShowWishlists(false);
    }
  };

  const handleConfirmNewWishlist = () => {
    setConfirmNewDialog(false);
    if (isContractor) {
      // Client was already chosen in handleClientSelect; proceed with them.
      setActiveStep(STEP.BUDGET);
    } else {
      setShowWishlists(false);
    }
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────────

  if (showWishlists) {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight={700}>My Service Wishlists</Typography>
          <Button variant="contained" onClick={handleNewWishlistClick}>
            + New Wishlist
          </Button>
        </Stack>

        {hasActiveWishlist && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have an active wishlist/contract. Creating a new one will inactivate the existing one and hide related service menus until the new one is approved.
          </Alert>
        )}

        {wishlistsLoading && <CircularProgress />}

        {wishlists.length === 0 && !wishlistsLoading && (
          <Paper sx={{ p: 4, textAlign: 'center', border: '2px dashed #e0e0e0', borderRadius: 3 }}>
            <Typography color="text.secondary">No wishlists yet. Create one using the wizard!</Typography>
          </Paper>
        )}

        <Grid container spacing={3}>
          <Paged items={wishlists}>{rows => rows.map(wl => (
            <Grid item xs={12} md={6} key={wl.id}>
              <Card elevation={2} sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight={600}>
                      {wl.forWhom === 'both' ? 'Bride & Groom' : wl.forWhom === 'bride' ? 'Bride Family' : 'Groom Family'}
                    </Typography>
                    <Chip
                      label={wl.status.replace(/_/g, ' ')}
                      color={
                        wl.status === 'approved' || wl.status === 'contracted' ? 'success'
                        : wl.status === 'pending_approval' ? 'info'
                        : wl.status === 'rejected' ? 'error'
                        : 'warning'
                      }
                      size="small"
                    />
                  </Stack>
                  {isContractor && (wl.user || wl.groomUser) && (
                    <Typography variant="body1" fontWeight={600} mb={0.5}>
                      For: {[wl.user, wl.groomUser].filter(Boolean).map(personName).join(' & ')}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {(wl.serviceSelections || []).length} service categories selected
                  </Typography>
                  {wl.totalBudget && (
                    <Typography variant="body2">Budget: ₹{Number(wl.totalBudget).toLocaleString()}</Typography>
                  )}
                  {wl.eventDate && (
                    <Typography variant="body2">Event Date: {wl.eventDate}</Typography>
                  )}
                  {wl.eventCity && (
                    <Typography variant="body2">Function Place: {wl.eventCity}</Typography>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                    {(wl.serviceSelections || []).map((cat, i) => (
                      <Chip key={i} label={cat.categoryName} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                    {(wl.status === 'draft') && (
                      <Button size="small" variant="contained" color="primary" onClick={() => handleSubmitForApproval(wl.id)}>
                        Submit for Approval
                      </Button>
                    )}
                    {wl.status === 'pending_confirmation' && (
                      <Button size="small" variant="contained" color="success" onClick={() => handleConfirm(wl.id)}>
                        Confirm & Submit
                      </Button>
                    )}
                    {wl.status === 'pending_approval' && (
                      <Chip label="Awaiting Admin Approval" color="info" variant="outlined" />
                    )}
                    {wl.status === 'rejected' && (
                      <Alert severity="error" sx={{ width: '100%', py: 0 }}>
                        Rejected: {wl.rejectionReason || 'No reason provided'}
                      </Alert>
                    )}
                    {(wl.status === 'approved' || wl.status === 'contracted') && wl.contract && (
                      <Tooltip title="View contract details">
                        <Chip
                          label={`Contract: ${wl.contract.refNumber || wl.contract.title}`}
                          color="success"
                          icon={<CheckCircleIcon />}
                          onClick={() => setContractView({ open: true, contract: wl.contract })}
                          clickable
                        />
                      </Tooltip>
                    )}
                    <Tooltip title={isContractor ? 'Duplicate this wishlist for a chosen client' : 'Duplicate this wishlist as a new draft'}>
                      <Button size="small" variant="outlined" color="inherit" startIcon={<ContentCopyIcon />}
                        onClick={() => handleDuplicateWishlist(wl)}>
                        Duplicate
                      </Button>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}</Paged>
        </Grid>

        <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>

        <ReplaceWishlistDialog
          open={confirmNewDialog}
          onClose={() => setConfirmNewDialog(false)}
          onConfirm={handleConfirmNewWishlist}
          subjectLabel="you already have"
        />

        <ContractPrintView
          open={contractView.open}
          onClose={() => setContractView({ open: false, contract: null })}
          contract={contractView.contract}
        />

        <Dialog open={duplicateDialog.open} onClose={() => setDuplicateDialog({ open: false, wishlist: null, forWhom: '', client: null, groomClient: null })} maxWidth="sm" fullWidth>
          <DialogTitle>Duplicate Wishlist For</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                Reusing the service selections from <strong>{duplicateDialog.wishlist?.forWhom === 'both' ? 'Bride & Groom' : duplicateDialog.wishlist?.forWhom === 'bride' ? 'Bride Family' : 'Groom Family'}</strong>.
                Choose who this new draft is for — defaults to the same client(s).
              </Typography>
              <FormControl fullWidth>
                <InputLabel>For Whom</InputLabel>
                <Select
                  value={duplicateDialog.forWhom}
                  label="For Whom"
                  onChange={e => setDuplicateDialog(d => ({ ...d, forWhom: e.target.value, client: null, groomClient: null }))}
                >
                  <MenuItem value="bride">Bride Family</MenuItem>
                  <MenuItem value="groom">Groom Family</MenuItem>
                  <MenuItem value="both">Both Families</MenuItem>
                </Select>
              </FormControl>

              {duplicateDialog.forWhom === 'both' ? (
                <>
                  <Autocomplete
                    options={clientUsers.filter(u => u.role?.name === 'bride_family')}
                    value={duplicateDialog.client}
                    onChange={(_, val) => setDuplicateDialog(d => ({ ...d, client: val }))}
                    getOptionLabel={clientLabel}
                    renderInput={(params) => <TextField {...params} label="Select Bride" fullWidth />}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                  <Autocomplete
                    options={clientUsers.filter(u => u.role?.name === 'groom_family')}
                    value={duplicateDialog.groomClient}
                    onChange={(_, val) => setDuplicateDialog(d => ({ ...d, groomClient: val }))}
                    getOptionLabel={clientLabel}
                    renderInput={(params) => <TextField {...params} label="Select Groom" fullWidth />}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </>
              ) : (
                <Autocomplete
                  options={clientUsers.filter(u => u.role?.name === (duplicateDialog.forWhom === 'groom' ? 'groom_family' : 'bride_family'))}
                  value={duplicateDialog.client}
                  onChange={(_, val) => setDuplicateDialog(d => ({ ...d, client: val }))}
                  getOptionLabel={clientLabel}
                  renderInput={(params) => <TextField {...params} label={duplicateDialog.forWhom === 'groom' ? 'Select Groom' : 'Select Bride'} fullWidth />}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDuplicateDialog({ open: false, wishlist: null, forWhom: '', client: null, groomClient: null })}>Cancel</Button>
            <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={handleConfirmDuplicate}>
              Duplicate
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {isContractor ? 'Wedding Service Planner' : 'My Wedding Service Wishlist'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isContractor
              ? 'Create a service wishlist on behalf of Bride or Groom family'
              : `Select the services you need for your ${isBride ? "bride's" : "groom's"} side — submit for admin approval`
            }
          </Typography>
        </Box>
        {wishlists.length > 0 && (
          <Button variant="outlined" startIcon={<PlaylistAddCheckIcon />} onClick={() => setShowWishlists(true)}>
            View Wishlists ({wishlists.length})
          </Button>
        )}
      </Stack>

      <Stepper activeStep={isContractor ? activeStep : activeStep - 1} sx={{ mb: 4 }}>
        {(isContractor ? STEPS : STEPS.slice(1)).map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* ─── STEP 0: FOR WHOM (only for vendor/matchmaker) ── */}
      {activeStep === STEP.WHO && isContractor && (
        <Box>
          <Typography variant="h5" fontWeight={600} mb={3} textAlign="center">
            Select Client (Bride/Groom)
          </Typography>

          {forWhom !== 'both' && (
            <>
              {/* Client picker */}
              <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3, maxWidth: 500, mx: 'auto' }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                  Choose the bride or groom you are creating this for:
                </Typography>
                <Autocomplete
                  options={clientUsers}
                  value={selectedClient}
                  onChange={(_, val) => handleClientSelect(val)}
                  getOptionLabel={(u) => {
                    const name = u.profile?.firstName
                      ? `${u.profile.firstName} ${u.profile.lastName || ''}`.trim()
                      : u.email;
                    const role = u.role?.name === 'bride_family' ? 'Bride' : 'Groom';
                    return `${name} (${role})`;
                  }}
                  groupBy={(u) => u.role?.name === 'bride_family' ? 'Bride Family' : 'Groom Family'}
                  renderInput={(params) => (
                    <TextField {...params} label="Search client by name or email" fullWidth />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
                {selectedClient && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Selected: <strong>{clientLabel(selectedClient)}</strong>{' '}
                    ({selectedClient.role?.name === 'bride_family' ? 'Bride Family' : 'Groom Family'})
                  </Alert>
                )}
              </Paper>

              <Divider sx={{ mb: 3 }} />

              <Typography variant="h6" fontWeight={600} mb={2} textAlign="center">
                Or select "For Whom" manually:
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} sm={4}>
                  <Card
                    elevation={forWhom === 'bride' ? 8 : 2}
                    sx={{ borderRadius: 4, border: forWhom === 'bride' ? '3px solid #e91e63' : '2px solid transparent', cursor: 'pointer', transition: '0.2s' }}
                  >
                    <CardActionArea onClick={() => handleForWhomSelect('bride')} sx={{ p: 4, textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 60, color: '#e91e63', mb: 2 }} />
                      <Typography variant="h6" fontWeight={700}>Bride Family</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Services for the bride's side
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card
                    elevation={forWhom === 'groom' ? 8 : 2}
                    sx={{ borderRadius: 4, border: forWhom === 'groom' ? '3px solid #1565c0' : '2px solid transparent', cursor: 'pointer', transition: '0.2s' }}
                  >
                    <CardActionArea onClick={() => handleForWhomSelect('groom')} sx={{ p: 4, textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 60, color: '#1565c0', mb: 2 }} />
                      <Typography variant="h6" fontWeight={700}>Groom Family</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Services for the groom's side
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card
                    elevation={2}
                    sx={{ borderRadius: 4, border: '2px solid transparent', cursor: 'pointer', transition: '0.2s' }}
                  >
                    <CardActionArea onClick={handleBothFamiliesClick} sx={{ p: 4, textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 60, color: '#7b1fa2', mb: 2 }} />
                      <Typography variant="h6" fontWeight={700}>Both Families</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Joint services for both sides
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>

              {!selectedClient && forWhom && (
                <Alert severity="info" sx={{ mt: 3, maxWidth: 500, mx: 'auto' }}>
                  No specific client selected. The wishlist will be created on your behalf. Select a client above to assign it directly.
                </Alert>
              )}
            </>
          )}

          {forWhom === 'both' && (
            <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3, maxWidth: 560, mx: 'auto' }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2} textAlign="center">
                Select both families for this joint wishlist
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2} textAlign="center">
                This wishlist and its approval process will be visible to both accounts.
              </Typography>
              <Stack spacing={2}>
                <Autocomplete
                  options={clientUsers.filter(u => u.role?.name === 'bride_family')}
                  value={selectedClient}
                  onChange={(_, val) => setSelectedClient(val)}
                  getOptionLabel={clientLabel}
                  renderInput={(params) => <TextField {...params} label="Select Bride" fullWidth />}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
                <Autocomplete
                  options={clientUsers.filter(u => u.role?.name === 'groom_family')}
                  value={selectedGroomClient}
                  onChange={(_, val) => setSelectedGroomClient(val)}
                  getOptionLabel={clientLabel}
                  renderInput={(params) => <TextField {...params} label="Select Groom" fullWidth />}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Stack>
              {selectedClient && selectedGroomClient && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Bride: <strong>{clientLabel(selectedClient)}</strong> · Groom: <strong>{clientLabel(selectedGroomClient)}</strong>
                </Alert>
              )}
              <Stack direction="row" justifyContent="space-between" mt={3}>
                <Button startIcon={<ArrowBackIcon />} onClick={handleCancelBothFamilies}>Back</Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  disabled={!selectedClient || !selectedGroomClient}
                  onClick={handleBothFamiliesContinue}
                >
                  Continue
                </Button>
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {/* ─── STEP 1: WEDDING BUDGET (asked before any service is chosen) ── */}
      {activeStep === STEP.BUDGET && (
        <Box>
          <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 3, maxWidth: 560, mx: 'auto' }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <AccountBalanceWalletIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>
                What is the total wedding budget?
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {forWhom === 'both' && selectedClient && selectedGroomClient
                ? `The overall budget for ${clientLabel(selectedClient)} & ${clientLabel(selectedGroomClient)}. `
                : selectedClient ? `The overall budget for ${clientLabel(selectedClient)}. ` : ''}
              It is set on the Budget page, and you'll give a budget for each service you select next.
            </Typography>
            <TextField
              label="Total Wedding Budget"
              type="number"
              fullWidth
              required
              autoFocus
              value={familyBudget}
              onChange={e => setFamilyBudget(e.target.value)}
              inputProps={{ min: 0 }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              helperText={totalBudgetNum > 0 ? inr(totalBudgetNum) : ' '}
            />
            <Box mt={2}>
              <EventCityField
                value={eventCity}
                onChange={setEventCity}
                required
                helperText={`Vendors within ${DEFAULT_RADIUS} km of this place are shown first`}
              />
            </Box>
            <TextField
              label="Expected Guests"
              type="number"
              fullWidth
              sx={{ mt: 2 }}
              value={eventDetails.guestCount}
              onChange={e => setEventDetails(d => ({ ...d, guestCount: e.target.value }))}
              inputProps={{ min: 0 }}
              helperText="Used to estimate per-plate services such as catering"
            />
            {isContractor && !selectedClient && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No client is selected, so this budget is not saved to a family's Budget page.
              </Alert>
            )}
          </Paper>

          {isContractor && wishlistTemplates.length > 0 && (
            <Paper elevation={1} sx={{ p: 3, mb: 2, borderRadius: 3, maxWidth: 560, mx: 'auto' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                <PlaylistAddIcon color="secondary" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Optional: Start from a Template
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Load a tradition's checklist as a starting point — you can uncheck anything not needed.
                These items are added on top of whatever services you pick in the next steps.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Wishlist Template</InputLabel>
                <Select
                  value={selectedTemplateId}
                  label="Wishlist Template"
                  onChange={e => handleTemplateSelect(e.target.value)}
                >
                  <MenuItem value=""><em>None — build manually</em></MenuItem>
                  {wishlistTemplates.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} {t.tradition ? `(${t.tradition})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {templateItems.length > 0 && (
                <Box mt={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      {templateItems.filter(i => i.selected).length} of {templateItems.length} items selected
                    </Typography>
                    <Typography variant="caption" fontWeight={700} color="primary.main">
                      Est. Total: ₹{templateItems.filter(i => i.selected).reduce((s, i) => s + (Number(i.cost) || 0), 0).toLocaleString('en-IN')}
                    </Typography>
                  </Stack>
                  <FormGroup sx={{ mt: 0.5, maxHeight: 260, overflowY: 'auto' }}>
                    {templateItems.map((item, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={<Checkbox size="small" checked={item.selected} onChange={() => toggleTemplateItem(idx)} />}
                        label={
                          <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                            <Typography variant="body2">{item.task}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                              ₹{Number(item.cost || 0).toLocaleString('en-IN')}
                            </Typography>
                          </Stack>
                        }
                        sx={{ width: '100%', mr: 0 }}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}
            </Paper>
          )}

          <Stack direction="row" justifyContent="space-between" mt={4}>
            {isContractor ? (
              <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(STEP.WHO)}>Back</Button>
            ) : <Box />}
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={proceedToServices}
              disabled={!(totalBudgetNum > 0) || !eventCity.trim()}
            >
              Next: Choose Services
            </Button>
          </Stack>
        </Box>
      )}

      {/* ─── STEP 2: SERVICE CATEGORY TILES ─────────────────── */}
      {activeStep === STEP.SERVICES && (
        <Box>
          <Typography variant="h5" fontWeight={600} mb={1} textAlign="center">
            What services do you need?
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3} textAlign="center">
            Select all the service categories you're interested in. We'll ask detailed questions for each.
          </Typography>

          {/* What the selected services typically cost against the budget */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 3, maxWidth: 760, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box flex={1}>
                <Typography variant="body2" fontWeight={600}>
                  Selected services typically cost {inr(estimatedSelected)} of your {inr(totalBudgetNum)} budget
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={totalBudgetNum > 0 ? Math.min(100, (estimatedSelected / totalBudgetNum) * 100) : 0}
                  color={estimatedSelected > totalBudgetNum ? 'error' : 'primary'}
                  sx={{ height: 6, borderRadius: 3, mt: 0.75 }}
                />
                {!guestCountNum && (
                  <Typography variant="caption" color="text.secondary">
                    Add the expected guests on the Budget step to include per-plate services like catering.
                  </Typography>
                )}
              </Box>
              <Button variant="contained" color="secondary" startIcon={<AutoFixHighIcon />} onClick={suggestServices} sx={{ flexShrink: 0 }}>
                Suggest services for my budget
              </Button>
            </Stack>
          </Paper>

          {templateItems.some(i => i.selected) && (
            <Alert severity="info" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              {templateItems.filter(i => i.selected).length} template item(s) are already included and will be added
              to this wishlist regardless of what you pick below.
            </Alert>
          )}

          {tilesLoading ? (
            <Stack alignItems="center" py={6}><CircularProgress /></Stack>
          ) : (
            <Grid container spacing={2}>
              {tiles.map(tile => {
                const Icon = ICON_MAP[tile.icon] || LocationOnIcon;
                const isSelected = selectedCategories.includes(tile.category);
                // Fits when its typical cost fits what the other selected services leave
                const est = estimateOf(tile.category);
                const leftForThis = totalBudgetNum - estimatedSelected + (isSelected ? est?.typicalCost || 0 : 0);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={tile.category}>
                    <Card
                      elevation={isSelected ? 6 : 1}
                      sx={{
                        borderRadius: 3,
                        border: isSelected ? '2px solid #7b1fa2' : '2px solid transparent',
                        bgcolor: isSelected ? '#f3e5f5' : '#fff',
                        transition: '0.2s',
                        cursor: 'pointer',
                        '&:hover': { elevation: 4, bgcolor: '#fce4ec' },
                      }}
                    >
                      <CardActionArea onClick={() => toggleCategory(tile.category)} sx={{ p: 2.5 }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box sx={{ bgcolor: isSelected ? '#7b1fa2' : '#f5f5f5', borderRadius: 2, p: 1.5, display: 'flex' }}>
                            <Icon sx={{ fontSize: 32, color: isSelected ? '#fff' : '#7b1fa2' }} />
                          </Box>
                          <Box flex={1}>
                            <Typography variant="subtitle1" fontWeight={600}>{tile.displayName}</Typography>
                            <Typography variant="caption" color="text.secondary">{tile.description}</Typography>
                            {est?.typicalCost > 0 && (
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap mt={0.5}>
                                <Typography variant="caption" fontWeight={600}>Typically {inr(est.typicalCost)}</Typography>
                                <Chip
                                  size="small"
                                  color={est.typicalCost <= leftForThis ? 'success' : 'warning'}
                                  variant="outlined"
                                  label={est.typicalCost <= leftForThis ? 'Fits your budget' : 'Over budget'}
                                  sx={{ height: 20, fontSize: 11 }}
                                />
                              </Stack>
                            )}
                            {!(est?.typicalCost > 0) && est?.perGuest && est.unitPriceFrom > 0 && (
                              <Typography variant="caption" fontWeight={600} display="block" mt={0.5}>
                                From {inr(est.unitPriceFrom)} {est.priceUnit} — add guests for a total
                              </Typography>
                            )}
                          </Box>
                          <Checkbox checked={isSelected} color="secondary" />
                        </Stack>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          <Stack direction="row" justifyContent="space-between" mt={4}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(STEP.BUDGET)}>Back to Budget</Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={proceedToSplit}
              disabled={selectedCategories.length === 0 && !templateItems.some(i => i.selected)}
            >
              {selectedCategories.length > 0
                ? `Next: Split Budget (${selectedCategories.length} selected)`
                : 'Next: Review'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* ─── STEP 3: SPLIT THE BUDGET ACROSS THE SELECTED SERVICES ── */}
      {activeStep === STEP.SPLIT && (
        <Box>
          <Typography variant="h5" fontWeight={600} mb={1} textAlign="center">
            How would you like to split the budget?
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3} textAlign="center">
            Give each selected service its share of the {inr(totalBudgetNum)} total wedding budget.
          </Typography>

          <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, maxWidth: 960, mx: 'auto' }}>
            {/* The total can be raised or lowered here too, without going back a step */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ sm: 'center' }} mb={2}>
              <Typography variant="subtitle1" fontWeight={700} flex={1}>Total Wedding Budget</Typography>
              <AmountStepper label="Total wedding budget" value={familyBudget} onChange={setFamilyBudget} />
              <Button size="small" variant="outlined" onClick={splitEqually} sx={{ width: { sm: 120 }, flexShrink: 0 }}>Split Equally</Button>
            </Stack>
            <Autocomplete
              multiple
              freeSolo
              options={KEYWORD_SUGGESTIONS}
              value={keywords}
              onChange={(_, v) => setKeywords(v.map(k => k.trim()).filter(Boolean))}
              renderTags={(value, getTagProps) => value.map((k, i) => <Chip size="small" label={k} {...getTagProps({ index: i })} key={k} />)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Keywords for choosing vendors"
                  placeholder="e.g. banana leaf, jasmine, candid — press Enter after each"
                  helperText="A preferred vendor is picked for each service from these keywords, the budget and the distance. You can change it."
                />
              )}
              sx={{ mb: 2 }}
            />
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={1.5}>
              {selectedCategories.map(cat => {
                const tile = tiles.find(t => t.category === cat);
                const Icon = ICON_MAP[tile?.icon] || LocationOnIcon;
                const amount = Number(serviceBudgets[cat]) || 0;
                const open = Boolean(showcaseOpen[cat]);
                return (
                  <Paper key={cat} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: open ? '#fcf7fd' : '#fff' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} alignItems={{ sm: 'center' }}>
                    <Stack direction="row" spacing={1} alignItems="center" flex={1} minWidth={0}>
                      <Icon color="secondary" />
                      <Box minWidth={0}>
                        <Typography variant="body1" fontWeight={600} noWrap>{tile?.displayName || cat}</Typography>
                        <Button
                          size="small"
                          sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={() => setShowcaseOpen(prev => ({ ...prev, [cat]: !open }))}
                        >
                          {open ? 'Hide' : 'See'} what's included
                        </Button>
                      </Box>
                    </Stack>
                    <AmountStepper
                      label={`${tile?.displayName || cat} budget`}
                      value={serviceBudgets[cat] || ''}
                      onChange={val => setServiceBudgets(prev => ({ ...prev, [cat]: val }))}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ width: { sm: 120 }, flexShrink: 0, textAlign: { sm: 'right' } }}>
                      {totalBudgetNum > 0 ? `${Math.round((amount / totalBudgetNum) * 100)}%` : ''}
                    </Typography>
                  </Stack>
                  <AutoVendorPicker
                    category={cat} eventCity={eventCity} budget={amount} guestCount={guestCountNum} keywords={keywords}
                    onPick={(pick) => handleAutoPick(cat, pick)}
                  />
                  <PreferredVendorLine
                    vendor={preferredVendors[cat]}
                    onChange={() => setShowcaseOpen(prev => ({ ...prev, [cat]: true }))}
                    onReset={() => resetVendorPick(cat)}
                  />
                  <Collapse in={open} unmountOnExit>
                    <Divider sx={{ my: 1.5 }} />
                    <ServiceShowcase
                      category={cat} displayName={tile?.displayName || cat} budget={amount} eventCity={eventCity}
                      guestCount={guestCountNum} keywords={keywords}
                      preferred={preferredVendors[cat]} onChoose={(choice) => chooseVendor(cat, choice)}
                    />
                  </Collapse>
                  </Paper>
                );
              })}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <LinearProgress
              variant="determinate"
              value={totalBudgetNum > 0 ? Math.min(100, (allocatedBudget / totalBudgetNum) * 100) : 0}
              color={allocatedBudget > totalBudgetNum ? 'error' : 'primary'}
              sx={{ height: 8, borderRadius: 4, mb: 1 }}
            />
            <Typography variant="body2" color={allocatedBudget > totalBudgetNum ? 'error.main' : 'text.secondary'}>
              {inr(allocatedBudget)} allocated ·{' '}
              {allocatedBudget > totalBudgetNum
                ? `${inr(allocatedBudget - totalBudgetNum)} over the total budget`
                : `${inr(totalBudgetNum - allocatedBudget)} not yet allocated`}
            </Typography>
            {allocatedBudget > totalBudgetNum && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setFamilyBudget(String(allocatedBudget))}>
                    Increase Total
                  </Button>
                }
              >
                Increase the total budget to {inr(allocatedBudget)}, or decrease a service's amount.
              </Alert>
            )}
          </Paper>

          <Stack direction="row" justifyContent="space-between" mt={4}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setActiveStep(STEP.SERVICES)}>Back to Services</Button>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={proceedToQuestions}>
              Next: Answer Questions
            </Button>
          </Stack>
        </Box>
      )}

      {/* ─── STEP 4: QUESTIONS PER CATEGORY ─────────────────── */}
      {activeStep === STEP.QUESTIONS && currentTile && (
        <QuestionTile
          category={currentCategory}
          tile={currentTile}
          budget={Number(serviceBudgets[currentCategory]) || 0}
          eventCity={eventCity}
          guestCount={guestCountNum}
          keywords={keywords}
          preferred={preferredVendors[currentCategory]}
          onAutoPick={(pick) => handleAutoPick(currentCategory, pick)}
          onChooseVendor={(choice) => chooseVendor(currentCategory, choice)}
          onResetVendor={() => resetVendorPick(currentCategory)}
          answers={answers[currentCategory] || {}}
          serviceSelections={serviceSelections[currentCategory] || []}
          onAnswer={(qId, val) => handleAnswer(currentCategory, qId, val)}
          onSetAnswers={(next) => setCategoryAnswers(currentCategory, next)}
          onMultiAnswer={(qId, opt) => handleMultiAnswer(currentCategory, qId, opt)}
          onToggleService={(svcId) => toggleServiceSelection(currentCategory, svcId)}
          onNext={nextCategory}
          onPrev={prevCategory}
          categoryIndex={currentCategoryIdx}
          totalCategories={selectedCategories.length}
        />
      )}

      {/* ─── STEP 5: REVIEW & CONFIRM ─────────────────────────── */}
      {activeStep === STEP.REVIEW && (
        <Box>
          <Typography variant="h5" fontWeight={600} mb={3} textAlign="center">
            Review Your Selections
          </Typography>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <AccountBalanceWalletIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>Budget</Typography>
              {eventCity.trim() && <Chip size="small" icon={<LocationOnIcon />} label={`Function at ${eventCity.trim()}`} sx={{ ml: 'auto' }} />}
            </Stack>
            <Grid container spacing={2}>
              {[
                ['Total Wedding Budget', inr(totalBudgetNum), 'text.primary'],
                ['Allocated to Services', inr(allocatedBudget), 'text.primary'],
                [allocatedBudget > totalBudgetNum ? 'Over the Total by' : 'Unallocated', inr(Math.abs(totalBudgetNum - allocatedBudget)),
                  allocatedBudget > totalBudgetNum ? 'error.main' : 'success.main'],
              ].map(([label, value, color]) => (
                <Grid item xs={12} sm={4} key={label}>
                  <Typography variant="h6" fontWeight={700} color={color}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Grid>
              ))}
            </Grid>
            {allocatedBudget > totalBudgetNum && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                The service budgets add up to more than the total wedding budget. You can go back and adjust them, or continue.
              </Alert>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>Event Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Event Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={eventDetails.eventDate}
                  onChange={e => setEventDetails(d => ({ ...d, eventDate: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Event Scale</InputLabel>
                  <Select
                    value={eventDetails.eventScale}
                    label="Event Scale"
                    onChange={e => setEventDetails(d => ({ ...d, eventScale: e.target.value }))}
                  >
                    <MenuItem value="intimate">Intimate (&lt;50)</MenuItem>
                    <MenuItem value="small">Small (50-100)</MenuItem>
                    <MenuItem value="medium">Medium (100-200)</MenuItem>
                    <MenuItem value="large">Large (200-400)</MenuItem>
                    <MenuItem value="grand">Grand (400+)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Expected Guests"
                  type="number"
                  fullWidth
                  value={eventDetails.guestCount}
                  onChange={e => setEventDetails(d => ({ ...d, guestCount: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Paper>

          {selectedCategories.map(cat => {
            const tile = tiles.find(t => t.category === cat);
            const selectedSvcs = serviceSelections[cat] || [];
            const catAnswers = answers[cat] || {};
            const Icon = ICON_MAP[tile?.icon] || LocationOnIcon;
            return (
              <Paper key={cat} sx={{ p: 3, mb: 2, borderRadius: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                  <Icon color="primary" />
                  <Typography variant="h6" fontWeight={600}>{tile?.displayName || cat}</Typography>
                  <Chip label={`${selectedSvcs.length} services`} size="small" />
                  <Chip label={`Budget ${inr(serviceBudgets[cat])}`} size="small" color="primary" variant="outlined" />
                </Stack>
                <PreferredVendorLine vendor={preferredVendors[cat]} />
                {selectedSvcs.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                    {selectedSvcs.map(svcId => {
                      const svc = tile?.services?.find(s => s.id === svcId);
                      return svc ? <Chip key={svcId} label={svc.name} size="small" variant="outlined" sx={{ mb: 0.5 }} /> : null;
                    })}
                  </Stack>
                )}
                {Object.keys(catAnswers).length > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      {Object.keys(catAnswers).length} questions answered
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}

          {templateItems.some(i => i.selected) && (
            <Paper sx={{ p: 3, mb: 2, borderRadius: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <PlaylistAddIcon color="secondary" />
                <Typography variant="h6" fontWeight={600}>
                  {wishlistTemplates.find(t => t.id === selectedTemplateId)?.name || 'Template'} (Template)
                </Typography>
                <Chip label={`${templateItems.filter(i => i.selected).length} items`} size="small" />
                <Chip
                  label={`₹${templateItems.filter(i => i.selected).reduce((s, i) => s + (Number(i.cost) || 0), 0).toLocaleString('en-IN')}`}
                  size="small" color="primary" variant="outlined"
                />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {templateItems.filter(i => i.selected).map((item, idx) => (
                  <Chip key={idx} label={`${item.task} — ₹${Number(item.cost || 0).toLocaleString('en-IN')}`} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                ))}
              </Stack>
            </Paper>
          )}

          <Stack direction="row" justifyContent="space-between" mt={4}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => {
              if (selectedCategories.length === 0) {
                setActiveStep(STEP.SERVICES);
              } else {
                setActiveStep(STEP.QUESTIONS);
                setCurrentCategoryIdx(selectedCategories.length - 1);
              }
            }}>
              {selectedCategories.length === 0 ? 'Back to Services' : 'Back to Questions'}
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<CheckCircleIcon />}
              onClick={handleSubmitWishlist}
            >
              Create Wishlist
            </Button>
          </Stack>
        </Box>
      )}

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>

      <ReplaceWishlistDialog
        open={confirmNewDialog}
        onClose={() => setConfirmNewDialog(false)}
        onConfirm={handleConfirmNewWishlist}
        subjectLabel={(() => {
          if (forWhom === 'both') {
            const brideActive = selectedClient && hasActiveWishlistFor(selectedClient.id);
            const groomActive = selectedGroomClient && hasActiveWishlistFor(selectedGroomClient.id);
            if (brideActive && groomActive) return 'the bride and groom you selected already have';
            if (brideActive) return `${clientLabel(selectedClient)} (bride) already has`;
            if (groomActive) return `${clientLabel(selectedGroomClient)} (groom) already has`;
            return 'this client already has';
          }
          return selectedClient ? `${clientLabel(selectedClient)} already has` : 'this client already has';
        })()}
      />
    </Box>
  );
}

// ─── REPLACE WISHLIST CONFIRMATION DIALOG ──────────────────────────────────────

function ReplaceWishlistDialog({ open, onClose, onConfirm, subjectLabel }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Replace Existing Wishlist?</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {subjectLabel} an active wishlist/contract. Creating a new one will:
        </Alert>
        <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
          <li>Inactivate the current wishlist and contract</li>
          <li>Hide the service menus until the new wishlist is approved</li>
          <li>Require fresh admin approval for the new services</li>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="warning" onClick={onConfirm}>
          Continue & Replace
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── PREFERRED VENDOR (picked automatically, or chosen by the family) ──────────

// Common things families look for; any other keyword can be typed
const KEYWORD_SUGGESTIONS = [
  'Banana leaf', 'Chettinad', 'Traditional Tamil', 'Live counters', 'Filter coffee', 'Jain',
  'Jasmine', 'Marigold', 'Temple style', 'Floral', 'Royal', 'LED',
  'Candid', 'Drone', 'Cinematic', 'Nadaswaram', 'Carnatic', 'AC hall',
];

// onChange: show the vendors to choose another; onReset: back to the automatic pick
function PreferredVendorLine({ vendor, onChange, onReset }) {
  if (!vendor) {
    return (
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        No vendor offers this service yet — your matchmaker will suggest one.
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mt={1}>
      <StarIcon fontSize="small" color="primary" />
      <Typography variant="body2">
        <strong>Preferred vendor:</strong> {vendor.title}{vendor.packageName ? ` · ${vendor.packageName}` : ''}
        {vendor.city ? ` (${vendor.city})` : ''}
      </Typography>
      <Chip size="small" variant="outlined" color={vendor.auto ? 'secondary' : 'primary'} label={vendor.auto ? 'Picked automatically' : 'Chosen by you'} />
      {vendor.auto && (vendor.reasons || []).map(r => <Chip key={r} size="small" label={r} sx={{ bgcolor: '#fff8e1' }} />)}
      {onChange && <Button size="small" onClick={onChange}>Change</Button>}
      {onReset && !vendor.auto && <Button size="small" onClick={onReset}>Use automatic pick</Button>}
    </Stack>
  );
}

// ─── AMOUNT STEPPER (− / amount / +) ──────────────────────────────────────────

const BUDGET_STEP = 10000;

function AmountStepper({ label, value, onChange }) {
  const amount = Number(value) || 0;
  const change = (delta) => onChange(String(Math.max(0, amount + delta)));
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}>
      <Tooltip title={`Decrease by ${inr(BUDGET_STEP)}`}>
        <span>
          <IconButton size="small" aria-label={`Decrease ${label}`} disabled={amount <= 0} onClick={() => change(-BUDGET_STEP)}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <TextField
        size="small"
        type="number"
        required
        fullWidth
        value={value}
        onChange={e => onChange(e.target.value)}
        inputProps={{ min: 0, step: BUDGET_STEP, 'aria-label': label }}
        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
      />
      <Tooltip title={`Increase by ${inr(BUDGET_STEP)}`}>
        <IconButton size="small" aria-label={`Increase ${label}`} onClick={() => change(BUDGET_STEP)}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

// ─── QUESTION TILE COMPONENT ──────────────────────────────────────────────────

// How a question is answered: radio is a single choice, checkbox a multiple choice, and
// textarea / number / date are typed
const questionKind = (q) => ({ radio: 'select', checkbox: 'multiselect', textarea: 'text', number: 'text', date: 'text' }[q.type] || q.type);

function QuestionTile({
  category, tile, budget, eventCity, guestCount, keywords, preferred, onAutoPick, onChooseVendor, onResetVendor,
  answers, serviceSelections, onAnswer, onSetAnswers, onMultiAnswer, onToggleService, onNext, onPrev, categoryIndex, totalCategories,
}) {
  // Only the questions and options that fit this service's budget; the budget question comes
  // answered from the budget itself (Split Budget step)
  const { data, loading } = useQuery(QUESTIONNAIRE_QUESTIONS_QUERY, {
    variables: { category, budget: budget > 0 ? budget : null, guestCount: guestCount > 0 ? guestCount : null },
    fetchPolicy: 'cache-and-network',
  });
  const questions = data?.questionnaireQuestions;
  // A question that depends on another is asked once that one has the answer it needs
  const byKey = Object.fromEntries((questions || []).map(q => [q.questionKey, q]));
  const visibleQuestions = (questions || []).filter(q => {
    if (!q.dependsOn || !byKey[q.dependsOn]) return true;
    const a = answers[byKey[q.dependsOn].id];
    return Array.isArray(a) ? a.includes(q.dependsOnValue) : !q.dependsOnValue || a === q.dependsOnValue;
  });

  // Keep the answers in step with the budget: budget answers filled in, answers to questions
  // or options no longer offered removed
  useEffect(() => {
    if (!questions) return;
    const next = {};
    questions.forEach(q => {
      if (q.autoAnswer) { next[q.id] = q.autoAnswer; return; }
      const a = answers[q.id];
      if (a === undefined) return;
      const kind = questionKind(q);
      if (kind === 'select' && (q.options || []).length) { if ((q.options || []).includes(a)) next[q.id] = a; return; }
      if (kind === 'multiselect') { const kept = (Array.isArray(a) ? a : []).filter(o => (q.options || []).includes(o)); if (kept.length) next[q.id] = kept; return; }
      next[q.id] = a;
    });
    if (JSON.stringify(next) !== JSON.stringify(answers)) onSetAnswers(next);
  }, [questions]);
  const Icon = ICON_MAP[tile?.icon] || LocationOnIcon;
  const [showcaseOpen, setShowcaseOpen] = useState(true);
  // The answers also pick the vendor ("Pure Vegetarian", "Chettinad"...)
  const preferences = Object.values(answers).flatMap(a => (Array.isArray(a) ? a : [a])).filter(a => typeof a === 'string' && a);

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 4, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Box sx={{ bgcolor: '#7b1fa2', borderRadius: 2, p: 1.5, display: 'flex' }}>
            <Icon sx={{ fontSize: 36, color: '#fff' }} />
          </Box>
          <Box flex={1}>
            <Typography variant="h5" fontWeight={700}>{tile.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">{tile.description}</Typography>
          </Box>
          {/* Set in the Split Budget step */}
          <Chip label={`Budget ${inr(budget)}`} color="primary" variant="outlined" />
          <Chip label={`${categoryIndex + 1} / ${totalCategories}`} color="secondary" />
        </Stack>

        <LinearProgress
          variant="determinate"
          value={((categoryIndex + 1) / totalCategories) * 100}
          sx={{ mb: 3, borderRadius: 2, height: 8 }}
        />

        {/* What this service typically includes, against its budget */}
        <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 3, bgcolor: '#fcf7fd' }}>
          <AutoVendorPicker
            category={category} eventCity={eventCity} budget={budget} guestCount={guestCount}
            keywords={keywords} preferences={preferences} onPick={onAutoPick}
          />
          <Box mb={1}>
            <PreferredVendorLine vendor={preferred} onReset={onResetVendor} />
          </Box>
          <Button
            size="small"
            sx={{ p: 0, mb: showcaseOpen ? 1.5 : 0, textTransform: 'none', fontWeight: 600 }}
            endIcon={showcaseOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowcaseOpen(o => !o)}
          >
            What's included in {tile.displayName}
          </Button>
          <Collapse in={showcaseOpen}>
            <ServiceShowcase
              category={category}
              displayName={tile.displayName}
              budget={budget}
              eventCity={eventCity}
              guestCount={guestCount}
              keywords={keywords}
              preferences={preferences}
              preferred={preferred}
              onChoose={onChooseVendor}
            />
          </Collapse>
        </Paper>

        {/* Sub-services selection as checkboxes */}
        {tile.services && tile.services.length > 0 && (
          <Box mb={4}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Select the specific services you need:
            </Typography>
            <Grid container spacing={1}>
              {tile.services.map(svc => (
                <Grid item xs={12} sm={6} md={4} key={svc.id}>
                  <Paper
                    elevation={serviceSelections.includes(svc.id) ? 3 : 0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: serviceSelections.includes(svc.id) ? '2px solid #7b1fa2' : '1px solid #e0e0e0',
                      bgcolor: serviceSelections.includes(svc.id) ? '#f3e5f5' : '#fafafa',
                      cursor: 'pointer',
                      transition: '0.2s',
                    }}
                    onClick={() => onToggleService(svc.id)}
                  >
                    <FormControlLabel
                      control={<Checkbox checked={serviceSelections.includes(svc.id)} color="secondary" size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{svc.name}</Typography>
                          {svc.basePrice > 0 && (
                            <Typography variant="caption" color="text.secondary">From ₹{Number(svc.basePrice).toLocaleString()}</Typography>
                          )}
                        </Box>
                      }
                      sx={{ m: 0, width: '100%' }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Questions */}
        {budget > 0 && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Showing the questions and choices that fit your {inr(budget)} budget for {tile.displayName}.
          </Typography>
        )}
        {loading && !questions ? (
          <CircularProgress />
        ) : (
          <Stack spacing={3}>
            {visibleQuestions.map(q => q.autoAnswer ? (
              <Paper key={q.id} variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: '#fcf7fd' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                  <Typography variant="subtitle2" fontWeight={600}>{q.question}</Typography>
                  <Chip label={q.autoAnswer} color="secondary" size="small" />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Answered from the {inr(budget)} you set for this service in Split Budget{guestCount > 0 && /plate|guest/i.test(q.question) ? ` (about ${inr(Math.round(budget / guestCount))} per guest)` : ''}. Change it there to change this.
                </Typography>
              </Paper>
            ) : (
              <Paper key={q.id} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fafafa' }}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  {q.question} {q.required && <span style={{ color: '#e91e63' }}>*</span>}
                </Typography>

                {questionKind(q) === 'text' && (
                  <TextField
                    fullWidth
                    size="small"
                    type={q.type === 'number' || q.type === 'date' ? q.type : 'text'}
                    multiline={q.type === 'textarea'}
                    minRows={q.type === 'textarea' ? 2 : undefined}
                    InputLabelProps={q.type === 'date' ? { shrink: true } : undefined}
                    value={answers[q.id] || ''}
                    onChange={e => onAnswer(q.id, e.target.value)}
                    placeholder={q.type === 'date' ? undefined : 'Type your answer...'}
                  />
                )}

                {questionKind(q) === 'select' && (
                  <RadioGroup
                    value={answers[q.id] || ''}
                    onChange={e => onAnswer(q.id, e.target.value)}
                  >
                    <Grid container spacing={1}>
                      {(q.options || []).map(opt => (
                        <Grid item xs={12} sm={6} key={opt}>
                          <Paper
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              border: answers[q.id] === opt ? '2px solid #7b1fa2' : '1px solid #e0e0e0',
                              bgcolor: answers[q.id] === opt ? '#f3e5f5' : '#fff',
                              cursor: 'pointer',
                            }}
                            onClick={() => onAnswer(q.id, opt)}
                          >
                            <FormControlLabel
                              value={opt}
                              control={<Radio size="small" color="secondary" />}
                              label={<Typography variant="body2">{opt}</Typography>}
                              sx={{ m: 0 }}
                            />
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>
                )}

                {questionKind(q) === 'multiselect' && (
                  <Grid container spacing={1}>
                    {(q.options || []).map(opt => {
                      const isChecked = (answers[q.id] || []).includes(opt);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={opt}>
                          <Paper
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              border: isChecked ? '2px solid #7b1fa2' : '1px solid #e0e0e0',
                              bgcolor: isChecked ? '#f3e5f5' : '#fff',
                              cursor: 'pointer',
                            }}
                            onClick={() => onMultiAnswer(q.id, opt)}
                          >
                            <FormControlLabel
                              control={<Checkbox checked={isChecked} size="small" color="secondary" />}
                              label={<Typography variant="body2">{opt}</Typography>}
                              sx={{ m: 0 }}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      <Stack direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBackIcon />} onClick={onPrev}>
          {categoryIndex === 0 ? 'Back to Services' : 'Previous Category'}
        </Button>
        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={onNext}>
          {categoryIndex === totalCategories - 1 ? 'Review Selections' : 'Next Category'}
        </Button>
      </Stack>
    </Box>
  );
}
